import React from "react";
import { useSelector } from "react-redux";

import { Alert } from "~/components/alert";
import { Diff } from "~/components/code/diff";
import { Editor } from "~/components/code/editor";
import { DiffHeader } from "~/components/diff-header";
import { Join } from "~/components/join";
import { JoinItem } from "~/components/join/item";
import { HourMinutesSeconds } from "~/tools/date-time/format/clock-time-format";
import { formatAbsoluteTime } from "~/tools/date-time/format/format-absolute-time";
import type { Time } from "~/tools/date-time/time";

import { selectTimestampDate } from "../../../../../app/state/view/timeline/TimelineSelectors";
import type { FullComponent } from "../../../../../common/openapi";
import { DrawerComponentTitle, ExplorationDrawer } from "../../../../../components/react/drawer/ExplorationDrawer";
import { ErrorScreen } from "../../../../../components/react/full-center-view-layout/error-screen";
import { LoadingSpinner } from "../../../../../components/react/loading-spinner/LoadingSpinner";
import { NotFound } from "../../../../../components/react/not-found/NotFound";

import { useQueryLastComponentChange } from "./useQueryLastComponentChange";
import { useQueryPodOwnerComponent } from "./useQueryPodOwnerComponent";

export interface ComponentLastChangeDrawerState {
	component: FullComponent;
	sourceProperties: undefined | string;
	lastUpdateTimestamp: undefined | Time;
}

export interface ComponentLastChangeDrawerProps {
	when: undefined | ComponentLastChangeDrawerState;
}

export const ComponentLastChangeDrawer: React.FC<ComponentLastChangeDrawerProps> = ({ when }) => {
	const { isLoading, error, data: ownerComponent } = useQueryPodOwnerComponent(when?.component.data.id);

	return (
		<ExplorationDrawer
			data-testid="component-last-change-drawer"
			headerTitle={({ component }) => {
				if (isLoading || error) {
					return null;
				}

				if (ownerComponent) {
					return (
						<DrawerComponentTitle
							component={ownerComponent}
							displayTypeName
							typeOfData="Last change on the pod's owner"
						/>
					);
				}

				return <DrawerComponentTitle component={component} displayTypeName typeOfData="Last change" />;
			}}
			when={when}
		>
			{({ component, sourceProperties, lastUpdateTimestamp }) => {
				if (error) {
					return <ErrorScreen error={error} noShadow />;
				}

				if (isLoading) {
					return <LoadingSpinner className="h-full" size="large" />;
				}

				return (
					<DrawerContent
						componentId={ownerComponent?.id ?? component.data.id!}
						lastUpdateTimestamp={lastUpdateTimestamp}
						sourceProperties={sourceProperties}
					/>
				);
			}}
		</ExplorationDrawer>
	);
};

const DrawerContent: React.FC<{
	componentId: number;
	sourceProperties: undefined | string;
	lastUpdateTimestamp: undefined | Time;
}> = ({ componentId, sourceProperties, lastUpdateTimestamp }) => {
	const { isLoading, error, diff } = useQueryLastComponentChange(componentId);

	if (error) {
		return <ErrorScreen error={error} noShadow />;
	}

	if (isLoading) {
		return <LoadingSpinner className="h-full" size="large" />;
	}

	if (diff != null) {
		return (
			<Join className="h-full" direction="vertical">
				<JoinItem>
					<DiffHeader modifiedTimestamp={diff.newTimestamp} originalTimestamp={diff.oldTimestamp} />
				</JoinItem>
				<JoinItem>
					<Diff height="auto" language="yaml" modified={diff.new} original={diff.old} />
				</JoinItem>
			</Join>
		);
	}

	if (!sourceProperties) {
		return <DiffNotFound />;
	}

	return (
		<div className="flex h-full flex-col">
			<Alert>
				We couldn’t find any other configuration prior to {formatAbsoluteTime(lastUpdateTimestamp).toString()}. Most
				likely the component has not been changed or the last change is older than six months.
			</Alert>

			<Editor className="mt-4" height="100%" language="yaml" readOnly value={sourceProperties} />
		</div>
	);
};

const DiffNotFound: React.FC = () => {
	const timestamp = useSelector(selectTimestampDate);
	const timestampFormatted = formatAbsoluteTime(timestamp, {
		clockTimeFormat: HourMinutesSeconds,
	}).toString();

	return (
		<NotFound header="We couldn’t find any change">
			There are no configuration changes in the last six months before {timestampFormatted}.
			<br />
			Most likely the component has not been changed or the last change is older than six months.
		</NotFound>
	);
};
